import React, { createContext, useContext, useState } from 'react';
import i18n from '../i18n';

// Import all language files
import en from '../language/en.json';
import tr from '../language/tr.json';
import vi from '../language/vi.json';
import ar from '../language/ar.json';
import mam from '../language/mam.json';
import es from '../language/es.json';
import zh from '../language/zh.json';
import prs from '../language/prs.json';
import lo from '../language/lo.json';
import ti from '../language/ti.json';
import km from '../language/km.json';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('selectedLanguage');
      if (saved) return saved;
    }
    return 'en';
  });

  const changeLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
    // Store in localStorage for persistence
    localStorage.setItem('selectedLanguage', languageCode);
    // Update i18n language
    i18n.changeLanguage(languageCode);
  };

  // Comprehensive translation function using JSON files
  const t = (key, section = 'common') => {
    const translations = {
      en,
      tr,
      vi,
      ar,
      mam,
      es,
      zh,
      prs,
      lo,
      ti,
      km
    };

    const currentTranslations = translations[currentLanguage] || translations.en;
    
    // Helper function to get nested value from object
    const getNestedValue = (obj, keyPath) => {
      return keyPath.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    };
    
    // Handle nested keys like "tour.welcome.title" or "auth.login"
    if (key.includes('.')) {
      const value = getNestedValue(currentTranslations, key);
      // Ensure we return a string, not an object
      if (value !== undefined && typeof value === 'string') {
        return value;
      }
      // Fallback to English
      const fallbackValue = getNestedValue(translations.en, key);
      if (fallbackValue !== undefined && typeof fallbackValue === 'string') {
        return fallbackValue;
      }
      // If we got an object, try to extract a meaningful value or return the key
      if (value !== undefined && typeof value === 'object') {
        console.warn(`Translation key "${key}" returned an object instead of a string. Returning key as fallback.`);
        return key;
      }
      return key;
    }
    
    // Handle section-based keys
    if (section && section !== 'common') {
      return currentTranslations[section]?.[key] || 
             translations.en[section]?.[key] || 
             key;
    }
    
    // Fallback to common section
    return currentTranslations.common?.[key] || 
           translations.en.common?.[key] || 
           key;
  };

  // Initialize language from localStorage on mount
  React.useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
      // Also update i18n language
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  const value = {
    currentLanguage,
    changeLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 